import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useDueSignupCount } from "../hooks/useDueSignupCount";
import { notify } from "./UI/DesignStandards/Notifications/notify";
import { getRedirectBehavior } from "../utility/routeUtils";
import { SignInLink } from "../utility/routeUtils";
import logo_dark from "../assets/logo/Logo for dark theme.svg";
import logo_light from "../assets/logo/Logo For Light theme.svg";
import OptimizedImage from "./SEO/OptimizedImage";
import { uiSpans, captureException } from "../utils/sentryUtils";
import AuthModal from "./AuthModal/AuthModal";
import ProductMenu from "./ProductPages/ProductMenu";
import { track } from "@vercel/analytics";

const jobLinkClass = (active) =>
  `px-3 py-2 text-base font-medium transition border-b-2 focus:outline-none focus:underline focus:underline-offset-4 ${
    active
      ? "border-brand-gold-ink text-brand-charcoal"
      : "border-transparent text-brand-charcoal hover:border-brand-gold-ink hover:text-brand-charcoal"
  }`;

const secondaryLinkClass = (active) =>
  `px-3 py-2 text-base font-medium transition border-b-2 focus:outline-none focus:underline focus:underline-offset-4 ${
    active
      ? "border-brand-gold-ink text-brand-muted"
      : "border-transparent text-brand-muted hover:border-brand-gold-ink hover:text-brand-charcoal"
  }`;

const mobileJobClass = (active) =>
  `px-4 py-3 text-base font-medium transition border-l-4 focus:outline-none focus:underline focus:underline-offset-4 ${
    active
      ? "border-brand-gold-ink text-brand-charcoal"
      : "border-transparent text-brand-charcoal hover:border-brand-gold-ink"
  }`;

const mobileSecondaryClass = (active) =>
  `px-4 py-3 text-base font-medium transition border-l-4 focus:outline-none focus:underline focus:underline-offset-4 ${
    active
      ? "border-brand-gold-ink text-brand-muted"
      : "border-transparent text-brand-muted hover:border-brand-gold-ink hover:text-brand-charcoal"
  }`;

const Navbar = () => {
  const { user, signOut } = useAuth();
  const dueCount = useDueSignupCount();
  const location = useLocation();
  const navigate = useNavigate();
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    setIsDarkMode(mediaQuery.matches);
    const handleChange = (e) => setIsDarkMode(e.matches);
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  const handleLogout = async () => {
    return uiSpans.buttonClick("Logout", async () => {
      try {
        await signOut();
        notify.success("Successfully signed out");

        const currentPath = window.location.pathname;
        const redirectBehavior = getRedirectBehavior(currentPath);
        navigate(redirectBehavior.destination);
      } catch (error) {
        captureException(error, {
          tags: {
            action: "logout",
            component: "Navbar",
          },
        });
        notify.error("Error signing out. Please try again.");
      }
    });
  };

  const handleRunTournament = (e) => {
    track("create_tournament_click", { location: "navbar" });
    if (!user) {
      e.preventDefault();
      setShowAuthModal(true);
      setMobileOpen(false);
    }
  };

  const isActive = (to) =>
    to === "/"
      ? location.pathname === "/"
      : location.pathname.startsWith(to) && to !== "/";

  const accountLinks = user
    ? [
        { label: "My Signups", to: "/signup/dashboard", dueCount },
        { label: "Organization", to: "/organization" },
        { label: "Profile", to: "/profile" },
      ]
    : [];

  const accountLinkLabel = (link) => (
    <span className="inline-flex items-center">
      {link.label}
      {link.dueCount > 0 ? (
        <span
          className="ml-1.5 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-xs font-display font-bold bg-brand-gold text-brand-charcoal rounded-full"
          aria-hidden="true"
        >
          {link.dueCount}
        </span>
      ) : null}
    </span>
  );

  return (
    <nav className="bg-brand-cream h-16 flex items-center px-4 w-full z-50 relative border-b border-brand-charcoal/10">
      <Link to="/" className="flex items-center h-16 mr-4 lg:mr-8 shrink-0 select-none">
        <OptimizedImage
          src={isDarkMode ? logo_dark : logo_light}
          alt="Bowling Tournament App Logo"
          width={120}
          height={40}
          className="h-12 w-auto"
        />
      </Link>

      <div className="hidden lg:flex flex-1 items-center justify-between min-w-0">
        <div className="flex space-x-2 items-center">
          <ProductMenu variant="desktop" />
          <Link
            to="/pricing"
            className={jobLinkClass(isActive("/pricing"))}
          >
            Plans
          </Link>
          <Link
            to="/create-tournament"
            onClick={handleRunTournament}
            className={jobLinkClass(isActive("/create-tournament"))}
          >
            Run a tournament
          </Link>
          <Link
            to="/tournaments"
            className={jobLinkClass(isActive("/tournaments"))}
          >
            Find tournaments
          </Link>
          <Link to="/support" className={secondaryLinkClass(isActive("/support"))}>
            Support
          </Link>
          {accountLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={secondaryLinkClass(isActive(link.to))}
              aria-label={
                link.dueCount > 0
                  ? `${link.label}, ${link.dueCount} due`
                  : link.label
              }
            >
              {accountLinkLabel(link)}
            </Link>
          ))}
        </div>
        <div className="flex space-x-2 items-center">
          {user ? (
            <button
              onClick={handleLogout}
              className="px-3 py-2 text-base font-medium text-brand-muted hover:text-brand-charcoal border-b-2 border-transparent hover:border-brand-gold-ink focus:outline-none focus:underline focus:underline-offset-4"
            >
              Sign Out
            </button>
          ) : (
            <>
              <SignInLink
                to="/signin"
                className={secondaryLinkClass(isActive("/signin"))}
              >
                Sign in
              </SignInLink>
              <Link to="/signup" className="btn-brand">
                Sign up
              </Link>
            </>
          )}
        </div>
      </div>

      <button
        className="lg:hidden ml-auto inline-flex flex-col items-center justify-center min-h-11 min-w-11 rounded focus:outline-none focus:ring-2 focus:ring-brand-gold-ink"
        aria-label="Open menu"
        onClick={() => setMobileOpen((v) => !v)}
      >
        <span className="block w-6 h-0.5 bg-brand-charcoal mb-1" />
        <span className="block w-6 h-0.5 bg-brand-charcoal mb-1" />
        <span className="block w-6 h-0.5 bg-brand-charcoal" />
      </button>

      {mobileOpen && (
        <div className="lg:hidden absolute top-16 left-0 w-full bg-brand-cream border-t border-brand-charcoal/10 z-50 shadow-lg">
          <div className="flex flex-col py-2">
            <ProductMenu
              variant="mobile"
              onNavigate={() => setMobileOpen(false)}
            />
            <Link
              to="/create-tournament"
              className={mobileJobClass(isActive("/create-tournament"))}
              onClick={handleRunTournament}
            >
              Run a tournament
            </Link>
            <Link
              to="/tournaments"
              className={mobileJobClass(isActive("/tournaments"))}
              onClick={() => setMobileOpen(false)}
            >
              Find tournaments
            </Link>
            <Link
              to="/support"
              className={mobileSecondaryClass(isActive("/support"))}
              onClick={() => setMobileOpen(false)}
            >
              Support
            </Link>
            {accountLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={mobileSecondaryClass(isActive(link.to))}
                onClick={() => setMobileOpen(false)}
                aria-label={
                  link.dueCount > 0
                    ? `${link.label}, ${link.dueCount} due`
                    : link.label
                }
              >
                {accountLinkLabel(link)}
              </Link>
            ))}
            {user ? (
              <button
                onClick={() => {
                  handleLogout();
                  setMobileOpen(false);
                }}
                className="px-4 py-3 text-base font-medium text-brand-muted hover:text-brand-charcoal border-l-4 border-transparent hover:border-brand-gold-ink focus:outline-none text-left"
              >
                Sign Out
              </button>
            ) : (
              <>
              <div onClick={() => setMobileOpen(false)}>
                <SignInLink
                  to="/signin"
                  className={mobileSecondaryClass(isActive("/signin"))}
                >
                  Sign in
                </SignInLink>
              </div>
                <Link
                  to="/signup"
                  className="mx-4 my-2 btn-brand text-center"
                  onClick={() => setMobileOpen(false)}
                >
                  Sign up
                </Link>
              </>
            )}
          </div>
        </div>
      )}

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />
    </nav>
  );
};

export default Navbar;
